class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.sku,body,vendor,tag`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        const products = data.resources.results.products || [];
        const resultsMarkup = this.createResultsMarkup(products, searchTerm);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

 createResultsMarkup(products, searchTerm) {
  if (!products.length) {
    return `<div class="predictive-search predictive-search--no-results">
      <p class="predictive-search__no-results-text">Ничего не найдено для "${searchTerm}"</p>
    </div>`;
  }

  let markup = `<style>
    .predictive-search {
      max-width: none !important;
      width: 100% !important;
    }
    .predictive-search .predictive-search__results-groups-wrapper {
      max-width: none !important;
      width: 100% !important;
      padding: 0 2rem !important;
    }
    .predictive-search__result-group {
      max-width: none !important;
      width: 100% !important;
    }
    #predictive-search-results-products-list {
      max-width: none !important;
      width: 100% !important;
    }
    #predictive-search-results-products-list .predictive-search__list-item {
      padding: 0 !important;
      margin: 0 !important;
      max-width: none !important;
    }
    #predictive-search-results-products-list .predictive-search__item--link-with-thumbnail {
      display: flex !important;
      align-items: center !important;
      padding: 0.4rem 1rem !important;
      width: 100% !important;
      max-width: none !important;
      box-sizing: border-box !important;
      border-radius: 6px !important;
      transition: background-color 0.15s ease !important;
    }
    #predictive-search-results-products-list .predictive-search__item--link-with-thumbnail:hover {
      background-color: #f5f5f5 !important;
    }
    .predictive-search__item-content {
      max-width: none !important;
      width: 100% !important;
    }
    .predictive-search__item-content p {
      margin-bottom: 0.1rem !important;
      margin-top: 0 !important;
      line-height: 1.2 !important;
      white-space: normal !important;
    }
    .predictive-search__item-content .price {
      margin-bottom: 0.1rem !important;
      margin-top: 0 !important;
    }
  </style>
  <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
    <div class="predictive-search__result-group">
      <p class="predictive-search__heading text-body">Products</p>
      <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="listbox">`;
  
  products.forEach((product, index) => {
    const url = product.url;
    const title = product.title;
    const image = product.image || '';
    const price = product.price ? `${parseFloat(product.price).toFixed(2).replace('.', ',')} EUR` : '';
    const vendor = product.vendor || '';
    
    // Извлекаем основной SKU из handle (URL)
    let mainSku = '';
    if (product.handle) {
      const handleMatch = product.handle.match(/(\d{6,})/);
      if (handleMatch && handleMatch[1]) {
        mainSku = handleMatch[1];
      }
    }
    
    // Извлекаем альтернативный артикул из описания
    let altSku = '';
    if (product.body) {
      const cleanBody = product.body.replace(/<[^>]*>/g, '');
      const altMatch = cleanBody.match(/Alternative part number[:\s]+(\d+)/i);
      if (altMatch && altMatch[1]) {
        altSku = altMatch[1];
      }
    }
    
    const tags = product.tags ? product.tags.slice(0, 2).join(', ') : '';
    
    markup += `<li id="predictive-search-option-product-${index}" class="predictive-search__list-item" role="option" aria-selected="false">
      <a href="${url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
        <div class="predictive-search__item-image-wrapper predictive-search__item-image-wrapper--product" style="flex-shrink: 0; width: 80px; height: 45px; background: #f0f0f0;">
          ${image ? `<img class="predictive-search__item-image" src="${image}" alt="${title}" loading="lazy" width="100" height="60" style="object-fit: cover; aspect-ratio: 16/9; display: block;">` : ''}
        </div>
        <div class="predictive-search__item-content predictive-search__item-content--product" style="margin-left: 1.5rem; flex: 1;">
          <p class="predictive-search__item-heading h5" style="overflow: visible; text-overflow: clip; font-size: 1.5rem;">${title}</p>
          ${mainSku ? `<p class="predictive-search__item-sku" style="font-size: 1.25rem; color: #777;">Part number: ${mainSku}</p>` : ''}
          ${altSku && altSku !== mainSku ? `<p class="predictive-search__item-alt-sku" style="font-size: 1.25rem; color: #777;">Alt. part no.: ${altSku}</p>` : ''}
          ${vendor ? `<p class="predictive-search__item-vendor caption-with-letter-spacing light" style="font-size: 0.9rem;">${vendor}</p>` : ''}
          ${price ? `<div class="price">
            <span class="price-item price-item--regular" style="font-size: 1.5rem; font-weight: 600; color: #000;">${price}</span>
          </div>` : ''}
          ${tags ? `<p class="predictive-search__item-tags" style="font-size: 1.2rem; color: #666;">${tags}</p>` : ''}
        </div>
      </a>
    </li>`;
  });
  
  markup += `</ul></div>
    <div class="predictive-search__search-for-button">
      <button class="predictive-search__item predictive-search__item--term link link--text" tabindex="-1" type="button">
        <span data-predictive-search-search-for-text">Show all results for "${searchTerm}"</span>
      </button>
    </div>
  </div>`;
  
  return markup;
}






  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
